/**
 * Build Your Kit — PDP "Complete your setup" multi-add (custom Padel USA).
 * <build-your-kit> wraps the kit list under the Add-to-Cart button on racket PDPs.
 *
 * Behaviour:
 *  - The racket is the locked anchor (always part of the kit). Add-ons (bag / overgrips
 *    / balls) are checkboxes, UNCHECKED by default.
 *  - "Add kit to cart" reads the CURRENTLY selected racket variant from the product form,
 *    dedupes everything against the live cart (no double-adds), then POSTs all items in one
 *    request to /cart/add.js and navigates to /cart — mirrors assets/kit-upsell.js /
 *    assets/pro-kit.js (proven, no cart-drawer-internals risk).
 *  - Attribution (hidden line-item property `_added_from`): add-ons carry `pdp-kit`; the racket
 *    carries `pdp-kit-racket` (2026-10-09) so a racket bought through the kit button is never
 *    counted as an upsell. Distinct from kit-upsell-popup / cart-essentials-addons.
 *  - 2026-10-09: at most `data-max-addons` add-on rows show (in server-rendered priority order);
 *    rows past the cap are hidden and backfill when a shown item is already in the cart.
 *  - Analytics: `pdp_kit_shown` (once, the visible slots + positions) and `pdp_kit_added` (the
 *    committed add-ons) go to window.dataLayer, a bubbling document CustomEvent and
 *    Shopify.analytics.publish (customer events) — the inputs for attach-rate per slot/position.
 *  - 2026-10-09: rows with a choice select (Floky sleeve colour + size) start with no variant;
 *    picking one sets the checkbox's variant/price/image and ticks it. A ticked row without a
 *    choice blocks the add and points the shopper at the select.
 *  - Add-on rows whose product is already in the cart are hidden (on load and on cart:update),
 *    so the kit never re-offers what the shopper already has.
 *
 * Docs: custom-code-padelusa/features/build-your-kit.md
 */
(() => {
  if (customElements.get('build-your-kit')) return;

  const CART_ADD_URL = window.Theme?.routes?.cart_add_url || '/cart/add.js';
  const CART_JS_URL = `${window.Theme?.routes?.cart_url || '/cart'}.js`;
  const CART_URL = window.Theme?.routes?.cart_url || '/cart';
  const SOURCE = 'pdp-kit';
  const ANCHOR_SOURCE = 'pdp-kit-racket';

  customElements.define(
    'build-your-kit',
    class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('[data-byk-submit]');
        this.totalEl = this.querySelector('[data-byk-total]');
        this.countEl = this.querySelector('[data-byk-count]');
        this.boxes = [...this.querySelectorAll('input[data-byk-item]')];
        if (!this.button) return;

        this.anchorVariantId = this.dataset.anchorVariantId || '';
        this.maxAddons = Number(this.dataset.maxAddons) || Infinity;
        this.shownTracked = false;
        this.anchorPriceCents = Number(this.dataset.anchorPriceCents) || 0;
        this.format = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.dataset.currency || 'USD',
        });

        this.boxes.forEach((box) => box.addEventListener('change', () => this.#onToggle(box)));
        this.querySelectorAll('[data-byk-choice]').forEach((select) =>
          select.addEventListener('change', () => this.#onChoice(select))
        );
        this.button.addEventListener('click', () => this.#addKit());
        this.onCartUpdate = (event) => this.#arrange(event.detail?.resource);
        document.addEventListener('cart:update', this.onCartUpdate);
        this.#refresh();
        this.#arrange();
      }

      disconnectedCallback() {
        if (this.onCartUpdate) document.removeEventListener('cart:update', this.onCartUpdate);
      }

      #rowParts(box) {
        const row = box.closest('.byk__row');
        return {
          row,
          select: row?.querySelector('[data-byk-choice]') || null,
          hint: row?.querySelector('[data-byk-choice-hint]') || null,
        };
      }

      /** Ticking a choice row before choosing opens the shopper's attention on the select. */
      #onToggle(box) {
        const { select, hint } = this.#rowParts(box);
        if (select && box.checked && !box.dataset.variantId) {
          select.focus();
          if (typeof select.showPicker === 'function') {
            try {
              select.showPicker();
            } catch (error) {
              /* showPicker needs a user gesture in some browsers — focus is enough */
            }
          }
        }
        if (hint && (!box.checked || box.dataset.variantId)) hint.hidden = true;
        this.#refresh();
      }

      #onChoice(select) {
        const row = select.closest('.byk__row');
        const box = row?.querySelector('input[data-byk-item]');
        if (!box) return;
        const option = select.selectedOptions[0];
        const priceEl = row.querySelector('[data-byk-choice-price]');
        const img = row.querySelector('.byk__media img');
        const hint = row.querySelector('[data-byk-choice-hint]');
        if (option && option.value) {
          box.dataset.variantId = option.value;
          box.dataset.priceCents = option.dataset.priceCents || '0';
          box.checked = true;
          if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price.trim();
          if (img && option.dataset.image) {
            img.src = option.dataset.image;
            img.removeAttribute('srcset');
          }
          if (hint) hint.hidden = true;
        } else {
          box.dataset.variantId = '';
          box.dataset.priceCents = '';
          box.checked = false;
        }
        this.#refresh();
      }

      /**
       * Show at most `maxAddons` rows, in DOM (priority) order, skipping products already in the cart;
       * the rest stay hidden. Uses the event's cart when given. Tracks the first arrangement shown.
       */
      async #arrange(cart) {
        let current = cart;
        if (!current || !Array.isArray(current.items)) {
          try {
            const res = await fetch(CART_JS_URL, { headers: { Accept: 'application/json' } });
            if (!res.ok) return;
            current = await res.json();
          } catch (error) {
            return;
          }
        }
        const inCart = new Set((current.items || []).map((line) => String(line.product_id)));
        const rows = [...this.querySelectorAll('.byk__row--addon')];
        let shown = 0;
        rows.forEach((row) => {
          const ids = (row.dataset.bykProductIds || '').split(/\s+/).filter(Boolean);
          const show = !ids.some((id) => inCart.has(id)) && shown < this.maxAddons;
          if (show) shown += 1;
          row.hidden = !show;
          if (!show) {
            const box = row.querySelector('input[data-byk-item]');
            if (box) box.checked = false;
          }
        });
        // Nothing left to add → the racket alone is not a kit; hide the module.
        this.hidden = shown === 0;
        this.#refresh();
        if (!this.shownTracked && shown > 0) {
          this.shownTracked = true;
          this.#track('pdp_kit_shown', {
            racket_product_id: this.dataset.anchorProductId || '',
            items: this.#visibleRows().map((row, index) => this.#rowInfo(row, index + 1)),
          });
        }
      }

      #visibleRows() {
        return [...this.querySelectorAll('.byk__row--addon')].filter((row) => !row.hidden);
      }

      #rowInfo(row, position) {
        const box = row.querySelector('input[data-byk-item]');
        return {
          slot: row.dataset.bykSlot || '',
          product_id: (row.dataset.bykProductIds || '').split(/\s+/)[0] || '',
          variant_id: box?.dataset.variantId || '',
          price_cents: Number(box?.dataset.priceCents) || 0,
          position,
        };
      }

      /** Fire-and-forget analytics (same contract as assets/kit-upsell.js #track) — never throws. */
      #track(name, detail) {
        try {
          const payload = { ...detail, placement: SOURCE };
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: name, ...payload });
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
          window.Shopify?.analytics?.publish?.(name, payload);
        } catch (error) {
          /* analytics must never break the kit */
        }
      }

      #checkedBoxes() {
        return this.boxes.filter((box) => box.checked && !box.disabled && !box.closest('.byk__row')?.hidden);
      }

      #refresh() {
        const checked = this.#checkedBoxes();
        const addonCents = checked.reduce((sum, box) => sum + (Number(box.dataset.priceCents) || 0), 0);
        const total = this.anchorPriceCents + addonCents;
        if (this.totalEl) this.totalEl.textContent = this.format.format(total / 100);
        if (this.countEl) this.countEl.textContent = String(checked.length);
        // Styling hook: the kit CTA stays a quiet outline until an add-on is chosen.
        this.toggleAttribute('data-has-addons', checked.length > 0);
      }

      /** Read the racket variant currently selected in the product form (falls back to the anchor). */
      #currentRacketVariantId() {
        const input = document.querySelector(
          'product-form-component [name="id"], product-form [name="id"], form[action*="/cart/add"] [name="id"]'
        );
        const fromForm = input && input.value ? String(input.value) : '';
        return fromForm || String(this.anchorVariantId || '');
      }

      async #cartVariantIds() {
        try {
          const res = await fetch(CART_JS_URL, { headers: { Accept: 'application/json' } });
          if (!res.ok) return new Set();
          const cart = await res.json();
          return new Set((cart.items || []).map((line) => String(line.variant_id)));
        } catch (error) {
          return new Set();
        }
      }

      async #addKit() {
        // A ticked choice row (Floky sleeve) needs its colour + size before anything is added.
        const missing = this.#checkedBoxes().find((box) => !box.dataset.variantId);
        if (missing) {
          const { select, hint } = this.#rowParts(missing);
          if (hint) hint.hidden = false;
          select?.focus();
          return;
        }

        this.button.disabled = true;
        this.button.setAttribute('aria-busy', 'true');

        const inCart = await this.#cartVariantIds();
        const items = [];

        const racketId = this.#currentRacketVariantId();
        if (racketId && !inCart.has(racketId)) {
          items.push({ id: Number(racketId), quantity: 1, properties: { _added_from: ANCHOR_SOURCE } });
        }

        this.#checkedBoxes().forEach((box) => {
          const id = String(box.dataset.variantId || '');
          if (id && !inCart.has(id)) {
            items.push({ id: Number(id), quantity: 1, properties: { _added_from: SOURCE } });
          }
        });

        if (items.length === 0) {
          window.location.href = CART_URL;
          return;
        }

        const visible = this.#visibleRows();
        const addedInfo = this.#checkedBoxes()
          .filter((box) => !inCart.has(String(box.dataset.variantId || '')))
          .map((box) => {
            const row = box.closest('.byk__row');
            return this.#rowInfo(row, visible.indexOf(row) + 1);
          });

        let added = false;
        try {
          const res = await fetch(CART_ADD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items }),
          });
          added = res.ok;
        } catch (error) {
          /* navigate regardless — the cart page shows the truth */
        }
        if (added) {
          this.#track('pdp_kit_added', {
            racket_product_id: this.dataset.anchorProductId || '',
            racket_added: items.some((item) => String(item.id) === String(racketId)),
            items: addedInfo,
          });
        }
        if (added && (await this.#offerAccessories(racketId))) {
          this.button.disabled = false;
          this.button.removeAttribute('aria-busy');
          return;
        }
        window.location.href = CART_URL;
      }

      /**
       * 2026-10-06: after a kit add, hand off to the kit-upsell popup (accessories → /cart) exactly
       * like a main Add to cart, instead of jumping straight to /cart. Mirrors the Racket Finder's
       * precedent: a `cart:update` event with a product-form source and the racket as the recap item.
       * The popup hides essentials already in the cart, so kit add-ons are never re-offered.
       * Returns false (caller navigates to /cart) when no popup with add-ons is on the page.
       */
      async #offerAccessories(racketId) {
        if (!document.querySelector('kit-upsell-dialog [data-kit-add]')) return false;
        let cart = null;
        try {
          const res = await fetch(CART_JS_URL, { headers: { Accept: 'application/json' } });
          if (res.ok) cart = await res.json();
        } catch (error) {
          return false;
        }
        if (!cart || !Array.isArray(cart.items)) return false;
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: cart,
              sourceId: SOURCE,
              data: { source: 'product-form-component', variantId: String(racketId), itemCount: cart.item_count },
            },
          })
        );
        return true;
      }
    }
  );
})();
